import { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
    Check, AlertTriangle, Info, XCircle,
    ChevronRight, Star, ShoppingCart, Bell,
    Package, Zap, Download, Trash2, Edit, Eye,
} from "lucide-react";
import { getThemeContext } from "../../components/providers/ThemeProvider.jsx";
import { confirmDelete, showAlert, showErrorAlert, askForName } from "../../components/HelperAlerts/HelpersAlerts.jsx";
import Toggle      from "../../components/ui/Toggle.jsx";
import Checkbox    from "../../components/ui/Checkbox.jsx";
import RangeSlider from "../../components/ui/RangeSlider.jsx";
import RadioGroup  from "../../components/ui/RadioGroup.jsx";
import { ScrubInput, SelectDropdown, InputSpinner, EditableTitle, SaveStatus, StatusBadge, CopyableField, ShippingEstimate, DigitalProductStatus, DigitalPackageStatus, ProcessSteps } from "../../components/ui/index.js";

// ─── Helper: sección con título ─────────────────────────────────────────────
function Section({ title, children, id }) {
    return (
        <section id={id} className="mb-16 scroll-mt-8">
            <h2 className="text-2xl font-bold mb-1 dark:text-white text-gray-900 border-b border-base-300 pb-2">
                {title}
            </h2>
            <div className="mt-6">{children}</div>
        </section>
    );
}

Section.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
};

// ─── Helper: bloque con etiqueta ─────────────────────────────────────────────
function Block({ label, children, className = "" }) {
    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            {label && <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-semibold">{label}</p>}
            <div className="flex flex-wrap items-center gap-3">{children}</div>
        </div>
    );
}

Block.propTypes = {
    label: PropTypes.string,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

// ─── Color swatch ────────────────────────────────────────────────────────────
function Swatch({ name, bg }) {
    return (
        <div className="flex flex-col items-center gap-1 w-20">
            <div className={`w-16 h-16 rounded-xl shadow-md ${bg}`} />
            <span className="text-xs text-center dark:text-gray-300 text-gray-600 leading-tight">{name}</span>
        </div>
    );
}

Swatch.propTypes = {
    name: PropTypes.string.isRequired,
    bg: PropTypes.string.isRequired,
};

// ─── DESIGN SYSTEM PAGE ──────────────────────────────────────────────────────
export default function DesignSystem() {
    const theme = getThemeContext();
    const [toggleVal, setToggleVal] = useState(false);
    const [toggleAdv, setToggleAdv] = useState(true);
    const [checkNFC, setCheckNFC]   = useState(false);
    const [checkEmail, setCheckEmail] = useState(true);
    const [checkMagnet, setCheckMagnet] = useState(false);
    const [rangeVal, setRangeVal] = useState(40);
    const [radioVal, setRadioVal] = useState("standard");
    const [dropdownVal, setDropdownVal] = useState("petg");
    const [dropdownWithIcon, setDropdownWithIcon] = useState("");
    const [inputVal, setInputVal] = useState("");
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [spinnerVal, setSpinnerVal] = useState(10);
    const [spinnerVal2, setSpinnerVal2] = useState(50);
    const [demoTitle, setDemoTitle] = useState("Llavero de Marta");

    const handleLoading = () => {
        setLoadingBtn(true);
        setTimeout(() => setLoadingBtn(false), 2000);
    };

    const navItems = ["Colores", "Tipografía", "Botones", "Tarjetas", "Formularios", "Alertas & Badges", "Estado", "Envío", "Digital"];
    const navIds   = ["colors", "typography", "buttons", "cards", "forms", "alerts", "status", "shipping", "digital"];

    return (
        <div className="h-full w-full overflow-y-auto overflow-x-hidden dark:bg-base-200 bg-gray-100 text-gray-900 dark:text-gray-100 relative">
            {/* ── Sticky Header ── */}
            <div className="sticky top-0 z-30 dark:bg-base-300/90 bg-white/90 backdrop-blur border-b border-base-300 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="font-black text-lg tracking-tight dark:text-white text-gray-900">
                        CUSTOMIZ<span className="text-red-500">3D</span>
                    </span>
                    <span className="badge badge-outline text-xs">Design System</span>
                </div>
                {/* Navegación interna */}
                <nav className="hidden md:flex items-center gap-1">
                    {navItems.map((item, i) => (
                        <a
                            key={item}
                            href={`#${navIds[i]}`}
                            className="btn btn-ghost btn-xs text-xs"
                        >
                            {item}
                        </a>
                    ))}
                </nav>
                <Link to="/" className="btn btn-outline btn-xs gap-1">
                    ← Volver al home
                </Link>
            </div>

            {/* ── Contenido ── */}
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">

                {/* ══ 1. COLORES ══════════════════════════════════════════════ */}
                <Section title="1 · Colores del tema" id="colors">
                    <div className="flex flex-col gap-6">
                        <Block label="Acciones principales">
                            <Swatch name="primary"         bg="bg-primary" />
                            <Swatch name="secondary"       bg="bg-secondary" />
                            <Swatch name="accent"          bg="bg-accent" />
                            <Swatch name="neutral"         bg="bg-neutral" />
                        </Block>
                        <Block label="Base (fondos)">
                            <Swatch name="base-100"  bg="bg-base-100 border border-base-300" />
                            <Swatch name="base-200"  bg="bg-base-200 border border-base-300" />
                            <Swatch name="base-300"  bg="bg-base-300 border border-base-300" />
                        </Block>
                        <Block label="Estado">
                            <Swatch name="info"      bg="bg-info" />
                            <Swatch name="success"   bg="bg-success" />
                            <Swatch name="warning"   bg="bg-warning" />
                            <Swatch name="error"     bg="bg-error" />
                        </Block>
                    </div>
                </Section>

                {/* ══ 2. TIPOGRAFÍA ═══════════════════════════════════════════ */}
                <Section title="2 · Tipografía" id="typography">
                    <div className="flex flex-col gap-4 glassCard rounded-2xl p-6">
                        <div><span className="badge badge-ghost mb-1">H1</span><h1 className="text-3xl sm:text-5xl font-black leading-tight break-words">Customiz3D — Diseño 3D</h1></div>
                        <div><span className="badge badge-ghost mb-1">H2</span><h2 className="text-4xl font-extrabold">La fábrica de tus ideas</h2></div>
                        <div><span className="badge badge-ghost mb-1">H3</span><h3 className="text-3xl font-bold">Objetos personalizados</h3></div>
                        <div><span className="badge badge-ghost mb-1">H4</span><h4 className="text-2xl font-semibold">Llaveros, imanes, posavasos</h4></div>
                        <div><span className="badge badge-ghost mb-1">H5</span><h5 className="text-xl font-medium">Tecnología NFC integrada</h5></div>
                        <div><span className="badge badge-ghost mb-1">H6</span><h6 className="text-base font-medium uppercase tracking-widest">Etiqueta de sección</h6></div>
                        <div className="divider my-0" />
                        <div><span className="badge badge-ghost mb-1">Body lg</span><p className="text-lg">Diseñamos y fabricamos objetos 3D únicos adaptados a tu marca o idea.</p></div>
                        <div><span className="badge badge-ghost mb-1">Body base</span><p className="text-base">Elige entre nuestro catálogo de stock o crea el tuyo con Customiz3r online.</p></div>
                        <div><span className="badge badge-ghost mb-1">Body sm</span><p className="text-sm text-gray-500 dark:text-gray-400">Texto secundario, descripciones cortas, etiquetas de formulario.</p></div>
                        <div><span className="badge badge-ghost mb-1">Caption</span><p className="text-xs text-gray-400 uppercase tracking-widest">Impresión 3D personalizada · Madrid, España</p></div>
                    </div>
                </Section>

                {/* ══ 3. BOTONES ══════════════════════════════════════════════ */}
                <Section title="3 · Botones" id="buttons">
                    <div className="flex flex-col gap-8">

                        {/* Variantes */}
                        <Block label="Variantes">
                            <button className="btn btn-primary">Primary</button>
                            <button className="btn btn-secondary">Secondary</button>
                            <button className="btn btn-accent">Accent</button>
                            <button className="btn btn-neutral">Neutral</button>
                            <button className="btn btn-ghost">Ghost</button>
                            <button className="btn btn-outline">Outline</button>
                            <button className="btn btn-link">Link</button>
                        </Block>

                        {/* Variantes outline con color */}
                        <Block label="Outline con color">
                            <button className="btn btn-outline btn-primary">Primary</button>
                            <button className="btn btn-outline btn-secondary">Secondary</button>
                            <button className="btn btn-outline btn-accent">Accent</button>
                            <button className="btn btn-outline btn-error">Error</button>
                        </Block>

                        {/* Tamaños */}
                        <Block label="Tamaños">
                            <button className="btn btn-primary btn-lg">Large</button>
                            <button className="btn btn-primary">Default</button>
                            <button className="btn btn-primary btn-sm">Small</button>
                            <button className="btn btn-primary btn-xs">XSmall</button>
                        </Block>

                        {/* Estado */}
                        <Block label="Estados">
                            <button className="btn btn-primary" disabled>Disabled</button>
                            <button className={`btn btn-primary ${loadingBtn ? "loading" : ""}`} onClick={handleLoading}>
                                {loadingBtn ? "Cargando..." : "Click → Loading"}
                            </button>
                            <button className="btn btn-primary btn-wide">Wide</button>
                            <button className="btn btn-primary btn-block w-48">Block</button>
                        </Block>

                        {/* Con iconos */}
                        <Block label="Con iconos">
                            <button className="btn btn-primary gap-2"><ShoppingCart size={16} /> Añadir al carrito</button>
                            <button className="btn btn-secondary gap-2"><Download size={16} /> Descargar</button>
                            <button className="btn btn-accent gap-2"><Star size={16} /> Favorito</button>
                            <button className="btn btn-outline btn-error gap-2"><Trash2 size={16} /> Eliminar</button>
                            <button className="btn btn-ghost btn-square"><Edit size={16} /></button>
                            <button className="btn btn-ghost btn-square"><Eye size={16} /></button>
                        </Block>

                        {/* Estado de color */}
                        <Block label="Semánticos">
                            <button className="btn btn-info gap-1"><Info size={15} /> Info</button>
                            <button className="btn btn-success gap-1"><Check size={15} /> Éxito</button>
                            <button className="btn btn-warning gap-1"><AlertTriangle size={15} /> Aviso</button>
                            <button className="btn btn-error gap-1"><XCircle size={15} /> Error</button>
                        </Block>
                    </div>
                </Section>

                {/* ══ 4. TARJETAS ═════════════════════════════════════════════ */}
                <Section title="4 · Tarjetas" id="cards">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                        {/* GlassCard */}
                        <div className="glassCard rounded-2xl p-5 flex flex-col gap-3">
                            <span className="badge badge-outline badge-sm">glassCard</span>
                            <h3 className="text-lg font-bold">Tarjeta de vidrio</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Efecto backdrop-blur con fondo semitransparente. Usada en el home.</p>
                            <button className="btn btn-primary btn-sm self-start mt-auto">Ver más</button>
                        </div>

                        {/* DaisyUI card */}
                        <div className="card bg-base-100 shadow-xl">
                            <div className="card-body gap-3">
                                <span className="badge badge-outline badge-sm">card DaisyUI</span>
                                <h3 className="card-title text-lg">Tarjeta estándar</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Componente card de DaisyUI con shadow y fondo base.</p>
                                <div className="card-actions mt-2">
                                    <button className="btn btn-secondary btn-sm">Acción</button>
                                </div>
                            </div>
                        </div>

                        {/* Card con imagen */}
                        <div className="card bg-base-100 shadow-xl overflow-hidden">
                            <figure className="h-36 bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center">
                                <Package size={48} className="text-primary opacity-60" />
                            </figure>
                            <div className="card-body gap-2 p-4">
                                <span className="badge badge-outline badge-sm">card + imagen</span>
                                <h3 className="card-title text-base">Llavero personalizado</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Con logo, texto e integración NFC.</p>
                                <div className="card-actions justify-between items-center mt-2">
                                    <span className="font-bold text-lg">€12.90</span>
                                    <button className="btn btn-primary btn-sm gap-1"><ShoppingCart size={13}/> Añadir</button>
                                </div>
                            </div>
                        </div>

                        {/* Card horizontal */}
                        <div className="card card-side bg-base-100 shadow-xl overflow-hidden md:col-span-2">
                            <figure className="w-32 shrink-0 bg-gradient-to-b from-secondary/20 to-primary/20 flex items-center justify-center">
                                <Zap size={36} className="text-secondary opacity-70" />
                            </figure>
                            <div className="card-body p-4 gap-1">
                                <span className="badge badge-outline badge-sm self-start">card horizontal</span>
                                <h3 className="card-title text-base">Customiz3r Online</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Diseña tu objeto 3D en minutos, sin software externo.</p>
                                <div className="card-actions mt-2">
                                    <button className="btn btn-secondary btn-sm gap-1">Probar ahora <ChevronRight size={13}/></button>
                                </div>
                            </div>
                        </div>

                        {/* Card stats */}
                        <div className="stats shadow flex-col md:flex-row">
                            <div className="stat">
                                <div className="stat-title text-xs">Pedidos</div>
                                <div className="stat-value text-2xl text-primary">142</div>
                                <div className="stat-desc">↗︎ 12% este mes</div>
                            </div>
                            <div className="stat">
                                <div className="stat-title text-xs">Clientes</div>
                                <div className="stat-value text-2xl text-secondary">89</div>
                                <div className="stat-desc">↗︎ 5 nuevos</div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ══ 5. FORMULARIOS ══════════════════════════════════════════ */}
                <Section title="5 · Formularios & Inputs" id="forms">
                    <div className="glassCard rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Input text */}
                        <div className="form-control gap-1">
                            <label className="label py-0"><span
                                className="label-text text-sm font-semibold">Input texto</span></label>
                            <input
                                type="text"
                                placeholder="Tu nombre o empresa…"
                                className="input input-bordered w-full"
                                value={inputVal}
                                onChange={e => setInputVal(e.target.value)}
                            />
                            <label className="label py-0"><span className="label-text-alt text-gray-400">Texto auxiliar de ayuda</span></label>
                        </div>

                        {/* Input con estado */}
                        <div className="form-control gap-1">
                            <label className="label py-0"><span className="label-text text-sm font-semibold">Estados de input</span></label>
                            <input type="text" placeholder="Error"
                                   className="input input-bordered input-error w-full mb-1"/>
                            <input type="text" placeholder="Éxito"
                                   className="input input-bordered input-success w-full mb-1"/>
                            <input type="text" placeholder="Desactivado" className="input input-bordered w-full"
                                   disabled/>
                        </div>

                        {/* SelectDropdown */}
                        <div className="form-control gap-1">
                            <label className="label py-0">
                                <span className="label-text text-sm font-semibold">SelectDropdown</span>
                            </label>
                            <SelectDropdown
                                className="w-full"
                                value={dropdownVal}
                                onChange={setDropdownVal}
                                placeholder="Elige un material…"
                                options={[
                                    { value: "pla",   label: "PLA" },
                                    { value: "petg",  label: "PETG" },
                                    { value: "flex",  label: "Filamento flexible" },
                                    { value: "metal", label: "Metal" },
                                ]}
                            />
                        </div>

                        {/* SelectDropdown con iconos */}
                        <div className="form-control gap-1">
                            <label className="label py-0">
                                <span className="label-text text-sm font-semibold">SelectDropdown con iconos</span>
                            </label>
                            <SelectDropdown
                                className="w-full"
                                value={dropdownWithIcon}
                                onChange={setDropdownWithIcon}
                                placeholder="Elige una categoría…"
                                options={[
                                    { value: "llavero",  label: "Llavero", icon: <Package size={14} /> },
                                    { value: "iman",     label: "Imán",    icon: <Star size={14} /> },
                                    { value: "especial", label: "Especial", icon: <Zap size={14} /> },
                                ]}
                            />
                        </div>

                        {/* Textarea */}
                        <div className="form-control gap-1">
                            <label className="label py-0"><span
                                className="label-text text-sm font-semibold">Textarea</span></label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Describe tu proyecto o necesidad especial…"
                                rows={3}
                            />
                        </div>

                        {/* Checkbox */}
                        <div className="flex flex-col gap-3">
                            <p className="text-sm font-semibold dark:text-gray-200 text-gray-700">Checkbox</p>
                            <Checkbox
                                checked={checkNFC}
                                onChange={setCheckNFC}
                                label="Añadir tecnología NFC"
                                color="primary"
                            />
                            <Checkbox
                                checked={checkEmail}
                                onChange={setCheckEmail}
                                label="Recibir presupuesto por email"
                                color="secondary"
                            />
                            <Checkbox
                                checked={checkMagnet}
                                onChange={setCheckMagnet}
                                label="Incluir imán integrado"
                                color="accent"
                            />
                            <Checkbox
                                checked={false}
                                label="Opción desactivada"
                                color="primary"
                                disabled
                            />
                        </div>

                        {/* Toggle + Radio */}
                        <div className="flex flex-col gap-3">
                            <p className="text-sm font-semibold dark:text-gray-200 text-gray-700">Toggle</p>
                            <Toggle
                                checked={toggleVal}
                                onChange={setToggleVal}
                                label={toggleVal ? "Activado" : "Desactivado"}
                                color="primary"
                            />
                            <Toggle
                                checked={toggleAdv}
                                onChange={setToggleAdv}
                                label="Modo avanzado"
                                color="accent"
                            />
                            <Toggle
                                checked={true}
                                label="Opción desactivada"
                                color="primary"
                                disabled
                            />
                            <p className="text-sm font-semibold dark:text-gray-200 text-gray-700 mt-2">Radio</p>
                            <RadioGroup
                                value={radioVal}
                                onChange={setRadioVal}
                                color="primary"
                                options={[
                                    {value: "standard", label: "Estándar"},
                                    {value: "premium", label: "Premium"},
                                    {value: "custom", label: "Personalizado", disabled: true},
                                ]}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <ScrubInput></ScrubInput>
                        </div>

                        {/* InputSpinner */}
                        <div className="md:col-span-2 border-t border-base-300 pt-4 flex flex-col gap-3">
                            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">InputSpinner — arrastre ◀▶ valor · ▲▼ paso</p>
                            <div className="flex flex-wrap items-end gap-6">
                                <div className="w-44">
                                    <InputSpinner
                                        label="Cantidad"
                                        value={spinnerVal}
                                        onChange={setSpinnerVal}
                                        min={0}
                                        max={100}
                                    />
                                </div>
                                <div className="w-44">
                                    <InputSpinner
                                        label="Con límites"
                                        value={spinnerVal2}
                                        onChange={setSpinnerVal2}
                                        min={0}
                                        max={200}
                                        initialStep={5}
                                    />
                                </div>
                                <div className="w-44">
                                    <InputSpinner
                                        label="Disabled"
                                        value={42}
                                        onChange={() => {}}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Range slider */}
                        <div className="md:col-span-2">
                            <RangeSlider
                                value={rangeVal}
                                onChange={setRangeVal}
                                label="Cantidad de unidades"
                                valueLabel=" uds."
                                color="primary"
                                marks={["0", "25", "50", "75", "100"]}
                            />
                        </div>

                        {/* SelectDropdown — tamaños y disabled */}
                        <div className="md:col-span-2 border-t border-base-300 pt-4 flex flex-col gap-3">
                            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">SelectDropdown — tamaños</p>
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="flex flex-col gap-1 w-40">
                                    <span className="text-xs text-gray-400">xs</span>
                                    <SelectDropdown size="xs" value="pla" onChange={() => {}} options={[{value:"pla",label:"PLA"},{value:"petg",label:"PETG"}]} />
                                </div>
                                <div className="flex flex-col gap-1 w-44">
                                    <span className="text-xs text-gray-400">sm</span>
                                    <SelectDropdown size="sm" value="petg" onChange={() => {}} options={[{value:"pla",label:"PLA"},{value:"petg",label:"PETG"}]} />
                                </div>
                                <div className="flex flex-col gap-1 w-48">
                                    <span className="text-xs text-gray-400">md (default)</span>
                                    <SelectDropdown size="md" value="pla" onChange={() => {}} options={[{value:"pla",label:"PLA"},{value:"petg",label:"PETG"}]} />
                                </div>
                                <div className="flex flex-col gap-1 w-52">
                                    <span className="text-xs text-gray-400">lg</span>
                                    <SelectDropdown size="lg" value="petg" onChange={() => {}} options={[{value:"pla",label:"PLA"},{value:"petg",label:"PETG"}]} />
                                </div>
                                <div className="flex flex-col gap-1 w-48">
                                    <span className="text-xs text-gray-400">disabled</span>
                                    <SelectDropdown disabled value="pla" onChange={() => {}} options={[{value:"pla",label:"PLA"},{value:"petg",label:"PETG"}]} />
                                </div>
                            </div>
                        </div>

                        {/* Ejemplo de formulario completo */}
                        <div className="md:col-span-2 mt-2 flex flex-col gap-3 border-t border-base-300 pt-4">
                            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Ejemplo:
                                formulario de contacto</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input type="text" placeholder="Nombre" className="input input-bordered input-sm"/>
                                <input type="email" placeholder="Email" className="input input-bordered input-sm"/>
                            </div>
                            <textarea placeholder="Mensaje…" className="textarea textarea-bordered textarea-sm"
                                      rows={2}/>
                            <div className="flex gap-2 justify-end">
                                <button className="btn btn-ghost btn-sm">Cancelar</button>
                                <button className="btn btn-primary btn-sm gap-1"><Check size={13}/> Enviar</button>
                            </div>
                        </div>
                    </div>

                    {/* Edición in-situ y estado de guardado (barra del Customiz3r) */}
                    <div className="mt-6 flex flex-col gap-3 border-t border-base-300 pt-4">
                        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">
                            EditableTitle & SaveStatus
                        </p>
                        <p className="text-sm text-gray-400">
                            Click, Enter o F2 para editar; Enter o perder el foco confirman, Escape descarta.
                            El estado de guardado se comunica sin interrumpir.
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <EditableTitle
                                value={demoTitle}
                                onCommit={(name) => setDemoTitle(name)}
                                placeholder="Diseño sin título"
                            />
                            <SaveStatus status="saved" lastSavedAt={Date.now()} />
                            <SaveStatus status="dirty" />
                            <SaveStatus status="saving" />
                            <SaveStatus status="error" onRetry={() => {}} />
                        </div>
                    </div>
                </Section>

                {/* ══ 6. ALERTAS & BADGES ═════════════════════════════════════ */}
                <Section title="6 · Alertas & Badges" id="alerts">
                    <div className="flex flex-col gap-6">

                        {/* Alertas */}
                        <Block label="Alertas">
                            <div className="w-full flex flex-col gap-2">
                                <div role="alert" className="alert alert-info"><Info size={16}/><span>Información: tu pedido está en camino.</span></div>
                                <div role="alert" className="alert alert-success"><Check size={16}/><span>¡Éxito! Tu diseño se ha guardado correctamente.</span></div>
                                <div role="alert" className="alert alert-warning"><AlertTriangle size={16}/><span>Aviso: esta acción no se puede deshacer.</span></div>
                                <div role="alert" className="alert alert-error"><XCircle size={16}/><span>Error: no se pudo conectar con el servidor.</span></div>
                            </div>
                        </Block>

                        {/* Badges */}
                        <Block label="Badges">
                            <span className="badge">Default</span>
                            <span className="badge badge-primary">Primary</span>
                            <span className="badge badge-secondary">Secondary</span>
                            <span className="badge badge-accent">Accent</span>
                            <span className="badge badge-ghost">Ghost</span>
                            <span className="badge badge-outline">Outline</span>
                            <span className="badge badge-info">Info</span>
                            <span className="badge badge-success">Success</span>
                            <span className="badge badge-warning">Warning</span>
                            <span className="badge badge-error">Error</span>
                        </Block>

                        <Block label="Badges — tamaños">
                            <span className="badge badge-primary badge-lg">Large</span>
                            <span className="badge badge-primary">Default</span>
                            <span className="badge badge-primary badge-sm">Small</span>
                            <span className="badge badge-primary badge-xs">XSmall</span>
                        </Block>

                        {/* Tooltip */}
                        <Block label="Tooltip">
                            <div className="tooltip tooltip-primary" data-tip="Esto es un tooltip">
                                <button className="btn btn-primary btn-sm">Hover aquí</button>
                            </div>
                            <div className="tooltip tooltip-secondary tooltip-bottom" data-tip="Tooltip abajo">
                                <button className="btn btn-secondary btn-sm">Tooltip bottom</button>
                            </div>
                        </Block>

                        {/* Progress */}
                        <Block label="Progress bar">
                            <div className="w-full flex flex-col gap-2">
                                <progress className="progress progress-primary w-full" value="70" max="100" />
                                <progress className="progress progress-secondary w-full" value="45" max="100" />
                                <progress className="progress progress-accent w-full" value="90" max="100" />
                                <progress className="progress w-full" />
                            </div>
                        </Block>

                        {/* Loading spinners */}
                        <Block label="Loading">
                            <span className="loading loading-spinner loading-lg text-primary" />
                            <span className="loading loading-dots loading-md text-secondary" />
                            <span className="loading loading-ring loading-md text-accent" />
                            <span className="loading loading-bars loading-sm text-success" />
                        </Block>
                    </div>
                </Section>

                {/* ── SweetAlert2 Helpers ─────────────────────────────────────────── */}
                {/* ══ 7. ESTADO & DATOS COPIABLES ═════════════════════════════ */}
                <Section title="7 · Estado & datos copiables" id="status">
                    <div className="flex flex-col gap-8">

                        {/* StatusBadge */}
                        <div className="flex flex-col gap-4">
                            <p className="text-sm text-base-content/60">
                                <code className="bg-base-200 px-1 rounded break-words">StatusBadge</code> — el significado
                                nunca depende sólo del color: siempre lleva texto y, opcionalmente, un icono.
                            </p>
                            <div className="glassCard rounded-2xl p-6 flex flex-col gap-6">
                                <Block label="Tonos">
                                    <StatusBadge label="Neutral"  tone="neutral" />
                                    <StatusBadge label="Info"     tone="info"    icon={<Info size={13} />} />
                                    <StatusBadge label="Éxito"    tone="success" icon={<Check size={13} />} />
                                    <StatusBadge label="Aviso"    tone="warning" icon={<AlertTriangle size={13} />} />
                                    <StatusBadge label="Error"    tone="error"   icon={<XCircle size={13} />} />
                                </Block>
                                <Block label="Outline">
                                    <StatusBadge label="Neutral" tone="neutral" outline />
                                    <StatusBadge label="Info"    tone="info"    outline />
                                    <StatusBadge label="Éxito"   tone="success" outline />
                                    <StatusBadge label="Aviso"   tone="warning" outline />
                                    <StatusBadge label="Error"   tone="error"   outline />
                                </Block>
                                <Block label="Tamaños">
                                    <StatusBadge label="xs" tone="info" size="xs" />
                                    <StatusBadge label="sm" tone="info" size="sm" />
                                    <StatusBadge label="md" tone="info" size="md" />
                                    <StatusBadge label="lg" tone="info" size="lg" />
                                </Block>
                                <Block label="Con tooltip explicativo (hover)">
                                    <StatusBadge
                                        label="Pendiente"
                                        tone="warning"
                                        icon={<Bell size={13} />}
                                        title="Explicación larga del estado, disponible al pasar el ratón."
                                    />
                                </Block>
                            </div>
                        </div>

                        {/* CopyableField */}
                        <div className="flex flex-col gap-4">
                            <p className="text-sm text-base-content/60">
                                <code className="bg-base-200 px-1 rounded break-words">CopyableField</code> — para un dato que
                                el usuario debe copiar literalmente. El botón sólo aparece si hay valor, tiene
                                nombre accesible y confirma el copiado.
                            </p>
                            <div className="glassCard rounded-2xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <CopyableField
                                    label="Valor normal"
                                    value="ABCD1234"
                                    helpText="Pulsa el icono para copiarlo."
                                />
                                <CopyableField
                                    label="Destacado"
                                    value="ABCD1234"
                                    emphasis
                                    helpText="Variante emphasis."
                                />
                                <CopyableField
                                    label="Sin monoespaciado"
                                    value="600 12 34 56"
                                    mono={false}
                                    helpText="mono={false} para textos legibles."
                                />
                                <CopyableField
                                    label="Con display distinto"
                                    value="12.50"
                                    display="12,50 €"
                                    mono={false}
                                    helpText="Copia el valor crudo, muestra el formateado."
                                />
                                <CopyableField
                                    label="Vacío"
                                    value=""
                                    emptyText="Pendiente"
                                    helpText="Sin valor no se ofrece copiar."
                                />
                                <CopyableField
                                    label="Disabled"
                                    value="ABCD1234"
                                    disabled
                                    helpText="Acción de copiar desactivada."
                                />
                            </div>
                        </div>
                    </div>
                </Section>

                <Section title="8 · Envío (ShippingEstimate)" id="shipping">
                    <p className="text-sm text-base-content/60 mb-4">
                        Resumen del envío físico del checkout. Importa desde{" "}
                        <code className="bg-base-200 px-1 rounded break-words">components/ui</code>. Presenta el
                        importe cotizado por el backend y el intervalo de entrega con lenguaje de estimación,
                        nunca de garantía. El coste, el peso y el servicio los fija siempre el servidor.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Sin dirección (idle)</p>
                            <ShippingEstimate state="idle" />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Calculando (loading)</p>
                            <ShippingEstimate state="loading" />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Disponible</p>
                            <ShippingEstimate
                                state="available" cost="6.05" currency="EUR"
                                deliveryMin="2026-08-25" deliveryMax="2026-08-26"
                            />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Pedido digital (sin envío)</p>
                            <ShippingEstimate state="digital" />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Indisponible + reintento</p>
                            <ShippingEstimate
                                state="unavailable"
                                message="El envío físico no cubre Baleares, Canarias, Ceuta ni Melilla; solo España peninsular."
                                onRetry={() => {}}
                            />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Caducada tras un cambio</p>
                            <ShippingEstimate state="expired" onRetry={() => {}} />
                        </div>
                        <div className="glassCard p-4 rounded-xl">
                            <p className="text-xs text-base-content/50 mb-2">Error de red + reintento</p>
                            <ShippingEstimate
                                state="error"
                                message="No se pudo contactar con el servicio de envío."
                                onRetry={() => {}}
                            />
                        </div>
                    </div>
                </Section>

                <Section title="9 · Producto digital (DigitalProductStatus)" id="digital">
                    <p className="text-sm text-base-content/60 mb-4">
                        Estado y precio del producto digital. Importa desde{" "}
                        <code className="bg-base-200 px-1 rounded break-words">components/ui</code>. Distingue
                        «precio calculado» de «precio final revisado», usa lenguaje de estimación y nunca ofrece
                        compra ni descarga (no comprable en MVP-04A).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <DigitalProductStatus state="empty" onCalculate={() => {}} />
                        <DigitalProductStatus state="calculando" />
                        <DigitalProductStatus state="pendiente_revision" calculatedPrice="0.50" currency="EUR" onCalculate={() => {}} />
                        <DigitalProductStatus state="aprobado" calculatedPrice="0.50" finalPrice="0.50" currency="EUR" />
                        <DigitalProductStatus state="aprobado" calculatedPrice="0.50" finalPrice="3.33" currency="EUR" overrideReason="ajuste comercial" />
                        <DigitalProductStatus state="rechazado" rejectionReason="geometría dudosa" onCalculate={() => {}} />
                        <DigitalProductStatus state="obsoleto" />
                        <DigitalProductStatus state="invalido" onCalculate={() => {}} />
                        <DigitalProductStatus state="error" onRetry={() => {}} />
                    </div>
                </Section>

                <Section title="10 · Paquete comercial (DigitalPackageStatus)" id="digital-package">
                    <p className="text-sm text-base-content/60 mb-4">
                        Estado del paquete digital comercial (MVP-04B): ZIP con 3MF, PDF de instrucciones
                        y, para lightbox cuando aplica, plantilla de montaje. Importa desde{" "}
                        <code className="bg-base-200 px-1 rounded break-words">components/ui</code>. Es un
                        componente hermano de <code className="bg-base-200 px-1 rounded break-words">DigitalProductStatus</code>,
                        no una variante suya: su eje de estado es la generación/revisión del paquete, no el
                        precio. Nunca ofrece compra ni descarga — «aprobado» aquí es un estado técnico.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <DigitalPackageStatus state="empty" />
                        <DigitalPackageStatus state="generando" />
                        <DigitalPackageStatus state="pendiente_revision" hasTemplate />
                        <DigitalPackageStatus state="aprobado" hasTemplate />
                        <DigitalPackageStatus state="rechazado" rejectionReason="imagen ilegible" />
                        <DigitalPackageStatus state="obsoleto" />
                        <DigitalPackageStatus state="invalido" lastError="No se pudo exportar la geometría comercial." />
                        <DigitalPackageStatus state="error" onRetry={() => {}} />
                    </div>
                </Section>

                <Section title="11 · Recorrido por pasos (ProcessSteps)" id="process-steps">
                    <p className="text-sm text-base-content/60 mb-4">
                        Explica en qué punto está un proceso, quién tiene que actuar y qué falta.
                        Importa desde <code className="bg-base-200 px-1 rounded break-words">components/ui</code>.
                        Lo usan el recorrido comercial del producto digital (propietario) y la revisión
                        del paquete (administración), para que ambos lean el mismo estado con el mismo
                        lenguaje. Estados por paso:{" "}
                        <code className="bg-base-200 px-1 rounded">done</code>,{" "}
                        <code className="bg-base-200 px-1 rounded">current</code>,{" "}
                        <code className="bg-base-200 px-1 rounded">blocked</code> (con{" "}
                        <code className="bg-base-200 px-1 rounded">reason</code>) y{" "}
                        <code className="bg-base-200 px-1 rounded">pending</code>. El estado nunca se
                        comunica solo por color: cada paso lleva su equivalente en texto para lectores
                        de pantalla.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Block label="Vertical, con paso bloqueado y acción">
                            <ProcessSteps
                                steps={[
                                    { id: "a", label: "Precio aprobado", state: "done" },
                                    { id: "b", label: "Accesorios confirmados", state: "done" },
                                    {
                                        id: "c",
                                        label: "Archivos generados",
                                        state: "current",
                                        actor: "Administración está preparando los archivos.",
                                    },
                                    {
                                        id: "d",
                                        label: "Licencia asignada",
                                        state: "blocked",
                                        reason: "No hay ninguna versión de licencia digital activa.",
                                        action: { label: "Configurar licencia", onClick: () => {} },
                                    },
                                    { id: "e", label: "Aprobación final", state: "pending" },
                                ]}
                            />
                        </Block>
                        <Block label="Horizontal y compacto">
                            <ProcessSteps
                                compact
                                orientation="horizontal"
                                steps={[
                                    { id: "1", label: "Precio calculado", state: "done" },
                                    { id: "2", label: "Precio aprobado", state: "done" },
                                    { id: "3", label: "Paquete preparado", state: "current" },
                                    { id: "4", label: "Licencia asignada", state: "pending" },
                                    { id: "5", label: "Listo para comprar", state: "pending" },
                                ]}
                            />
                        </Block>
                    </div>
                </Section>

                <Section title="Diálogos (SweetAlert2)" id="dialogs">
                    <p className="text-sm text-base-content/60 mb-4">
                        Importa desde <code className="bg-base-200 px-1 rounded break-words">components/HelperAlerts/HelpersAlerts.jsx</code>.
                        Todos los helpers aceptan <code className="bg-base-200 px-1 rounded break-words">theme</code> (obtenido con <code className="bg-base-200 px-1 rounded break-words">getThemeContext()</code>) para adaptar fondo y colores al tema activo.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Block label="showAlert — éxito">
                            <button
                                className="btn btn-success btn-sm"
                                onClick={() => showAlert({ title: "¡Guardado!", text: "El cambio se aplicó correctamente.", icon: "success", theme })}
                            >
                                Probar éxito
                            </button>
                        </Block>
                        <Block label="showAlert — error">
                            <button
                                className="btn btn-error btn-sm"
                                onClick={() => showErrorAlert({ title: "Error", text: "Algo salió mal.", theme })}
                            >
                                Probar error
                            </button>
                        </Block>
                        <Block label="confirmDelete">
                            <button
                                className="btn btn-warning btn-sm"
                                onClick={async () => {
                                    const ok = await confirmDelete({ title: "¿Eliminar?", text: "Esta acción no se puede deshacer.", theme });
                                    if (ok) showAlert({ title: "Eliminado", icon: "success", theme });
                                }}
                            >
                                Probar confirmación
                            </button>
                        </Block>
                        <Block label="askForName">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={async () => {
                                    const name = await askForName({ title: "Renombrar", currentName: "Mi modelo", theme });
                                    if (name) showAlert({ title: `Nuevo nombre: ${name}`, icon: "info", theme });
                                }}
                            >
                                Probar input
                            </button>
                        </Block>
                    </div>
                </Section>

                {/* Footer de la página */}
                <div className="border-t border-base-300 pt-8 pb-4 text-center text-sm text-gray-400">
                    Customiz3D Design System · Tailwind CSS + DaisyUI ·{" "}
                    <span className="font-semibold">{theme === "dark" ? "🌙 Dark theme" : "☀️ Light theme"}</span>
                </div>
            </div>
        </div>
    );
}
